"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

/* ✅ API data type */
type TeamMember = {
  id: number;
  name: string;
  designation: string;
  description: string;
  image: string;
};

export default function TeamGrid({ initialData }: { initialData?: TeamMember[] }) {
  const fetchedRef = useRef(false);
  const [team, setTeam] = useState<TeamMember[]>(initialData || []);

  useEffect(() => {
    if (team.length > 0) return; // Skip if provided by props
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchTeams = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/teams");
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }
        const json = (await res.json()) as { data: TeamMember[] };

        // ✅ Sort so ID 1 comes first
        const sortedData = json.data.sort((a, b) => a.id - b.id);

        setTeam(sortedData);
      } catch (error) {
        console.error("Failed to load team data", error);
      }
    };

    fetchTeams();
  }, [team]);

  return (
    <section className="py-16 bg-white font-outfit">
      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">
          Our Team
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Meet the Faces
        </h2>
        <h3 className="text-3xl sm:text-4xl font-bold text-[#0B2149]">
          Behind the Structure
        </h3>
      </div>

      {/* Grid Layout */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
        {team.map((member) => (
          <div
            key={member.id}
            className="flex flex-col bg-white rounded-lg shadow-md border overflow-hidden border-[#838383] hover:shadow-lg transition-all duration-300"
          >
            {/* Image */}
            <div className="relative w-full h-80 sm:h-96 group overflow-hidden">
              <div className="relative w-full h-full flex justify-center items-end">
                <Image
                  src={member.image}
                  alt={member.name}
                  fill
                  className="object-cover object-top z-10 transition-transform duration-500 ease-in-out group-hover:scale-110"
                />
              </div>
            </div>

            {/* Text */}
            <div className="p-6 text-center flex flex-col flex-grow">
              <h4 className="text-lg font-semibold text-[#2a6380] mb-1">
                {member.name}
              </h4>
              <p className="text-sm font-medium text-[#000000] mb-2">
                {member.designation}
              </p>
              <p className="text-[#000000] text-sm leading-relaxed">
                {member.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
